import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data"],
  _excluded2 = ["purchaseableProducts"],
  _excluded3 = ["purchaseOptionsForApiName"],
  _excluded4 = ["purchaseOptionsForTerm"],
  _excluded5 = ["releaseInsights", "loading", "error"],
  _excluded6 = ["releaseInsights", "loading", "error"];
import { useQuery } from 'data-fetching-client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GET_ALL_PRICING_PURCHASABLE_PRODUCTS } from 'pricing-pages-api/purchase-options/queries';
import { getI18nLanguage } from 'pricing-pages-lib/utils/languageUtils';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import { useSetOneGlobalLoadingState } from '../context/GlobalLoadingStateContext';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { useCountryCode, useDisplayedCurrencyCode } from './useCurrencies';
export const useQueryVariables = () => {
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const isAppAuth = useSelector(isAppAuthSelector);
  const language = getI18nLanguage() || 'en';
  const preferredCountryCode = useCountryCode();
  const memoizedVariables = useMemo(() => {
    return {
      noAuth: !isAppAuth,
      currencyCode: displayedCurrencyCode,
      language,
      preferredCountryCode: preferredCountryCode
    };
  }, [displayedCurrencyCode, isAppAuth, language, preferredCountryCode]);
  return memoizedVariables;
};
export const useBulkPurchaseOptions = ({
  onCompleted,
  onError,
  skip,
  variables
} = {}) => {
  var _data$bulkPurchaseOpt, _data$bulkPurchaseOpt2;
  const defaultQueryVariables = useQueryVariables();
  const onCompletedCallback = useSetOneGlobalLoadingState('PurchaseOptions', onCompleted);
  const _useQuery = useQuery(GET_ALL_PRICING_PURCHASABLE_PRODUCTS, {
      onCompleted: onCompletedCallback,
      onError,
      skip: skip || !defaultQueryVariables.currencyCode || !defaultQueryVariables.language,
      variables: Object.assign({}, defaultQueryVariables, variables)
    }),
    {
      data
    } = _useQuery,
    queryResult = _objectWithoutPropertiesLoose(_useQuery, _excluded);
  return Object.assign({
    purchaseableProducts: data === null || data === void 0 || (_data$bulkPurchaseOpt = data.bulkPurchaseOptions) === null || _data$bulkPurchaseOpt === void 0 ? void 0 : _data$bulkPurchaseOpt.allPurchaseableProducts,
    releaseInsights: data === null || data === void 0 || (_data$bulkPurchaseOpt2 = data.bulkPurchaseOptions) === null || _data$bulkPurchaseOpt2 === void 0 ? void 0 : _data$bulkPurchaseOpt2.releaseInsights
  }, queryResult);
};
export const usePurchaseableProductsForApiName = ({
  apiName
}) => {
  var _purchaseableProducts;
  const _useBulkPurchaseOptio = useBulkPurchaseOptions({
      skip: !apiName
    }),
    {
      purchaseableProducts
    } = _useBulkPurchaseOptio,
    queryResult = _objectWithoutPropertiesLoose(_useBulkPurchaseOptio, _excluded2);
  if (!apiName) return Object.assign({
    purchaseOptionsForApiName: null
  }, queryResult);
  const purchaseOptionsForApiName = apiName && (purchaseableProducts === null || purchaseableProducts === void 0 || (_purchaseableProducts = purchaseableProducts[apiName]) === null || _purchaseableProducts === void 0 ? void 0 : _purchaseableProducts.purchaseOptions);
  return Object.assign({
    purchaseOptionsForApiName
  }, queryResult);
};
export const usePurchaseableProductsForTerm = ({
  apiName,
  queryParamTerm
}) => {
  const _usePurchaseableProdu = usePurchaseableProductsForApiName({
      apiName
    }),
    {
      purchaseOptionsForApiName
    } = _usePurchaseableProdu,
    queryResult = _objectWithoutPropertiesLoose(_usePurchaseableProdu, _excluded3);
  const purchaseOptionsForTerm = useMemo(() => (purchaseOptionsForApiName === null || purchaseOptionsForApiName === void 0 ? void 0 : purchaseOptionsForApiName.find(purchaseOption => {
    var _purchaseOption$offer;
    return (purchaseOption === null || purchaseOption === void 0 || (_purchaseOption$offer = purchaseOption.offerInfo) === null || _purchaseOption$offer === void 0 ? void 0 : _purchaseOption$offer.payment) === queryParamTerm;
  })) || (purchaseOptionsForApiName === null || purchaseOptionsForApiName === void 0 ? void 0 : purchaseOptionsForApiName[0]), [purchaseOptionsForApiName, queryParamTerm]);
  return Object.assign({
    purchaseOptionsForTerm
  }, queryResult);
};
export const useMonthlyCalculatedPrice = ({
  apiName,
  queryParamTerm
}) => {
  const _usePurchaseableProdu2 = usePurchaseableProductsForTerm({
      apiName,
      queryParamTerm
    }),
    {
      purchaseOptionsForTerm
    } = _usePurchaseableProdu2,
    queryResult = _objectWithoutPropertiesLoose(_usePurchaseableProdu2, _excluded4);
  return Object.assign({
    monthlyCalculatedPrice: purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.monthly.calculatedPrice
  }, queryResult);
};
export const useHighestPossibleMonthlyPrice = ({
  apiName
}) => {
  const {
    monthlyCalculatedPrice: annualCalculatedPrice = 0
  } = useMonthlyCalculatedPrice({
    apiName,
    queryParamTerm: ANNUAL
  });
  const {
    monthlyCalculatedPrice = 0
  } = useMonthlyCalculatedPrice({
    apiName,
    queryParamTerm: MONTHLY
  });
  return {
    highestPossibleMonthlyPrice: annualCalculatedPrice > monthlyCalculatedPrice ? annualCalculatedPrice : monthlyCalculatedPrice
  };
};
export const useReleaseInsights = ({
  onCompleted,
  onError,
  skip
}) => {
  const _useBulkPurchaseOptio2 = useBulkPurchaseOptions({
      onCompleted,
      onError,
      skip
    }),
    {
      releaseInsights,
      loading,
      error
    } = _useBulkPurchaseOptio2,
    queryResult = _objectWithoutPropertiesLoose(_useBulkPurchaseOptio2, _excluded5);
  return Object.assign({
    releaseInsights,
    loading,
    error
  }, queryResult);
};
export const useHasReleaseInsight = ({
  onCompleted,
  onError,
  skip,
  releaseInsight
}) => {
  const _useReleaseInsights = useReleaseInsights({
      onCompleted,
      onError,
      skip: !releaseInsight || skip
    }),
    {
      releaseInsights,
      loading,
      error
    } = _useReleaseInsights,
    queryResult = _objectWithoutPropertiesLoose(_useReleaseInsights, _excluded6);
  const hasReleaseInsight = !!(releaseInsights !== null && releaseInsights !== void 0 && releaseInsights[releaseInsight]);
  return Object.assign({
    hasReleaseInsight,
    loading,
    error
  }, queryResult);
};